import { motion, MotionProps } from 'framer-motion';
import styled from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type SlidingProgressBarProps = {
  variant: 'error' | 'success' | 'info';
  duration?: number;
  onDone?: () => void;
};

export function SlidingProgressBar({ variant, duration = 5.5, onDone }: SlidingProgressBarProps) {
  return (
    <SlidingContainer aria-label={`Progress ${variant} for ${duration} seconds`}>
      <SlidingBar
        initial={{ maxWidth: '0%' }}
        animate={{ maxWidth: '100%', transition: { duration } }}
        onAnimationComplete={onDone}
        data-variant={variant}
      />
    </SlidingContainer>
  );
}

export type ProgressBarProps = MotionProps & {
  ratio: number;
  variant?: 'error' | 'paused' | 'default' | 'info';
};

export function ProgressBar({ ratio, style, variant = 'default', ...rest }: ProgressBarProps) {
  const percentage = ratio * 100;
  return (
    <Container aria-label={'Progress ' + percentage}>
      <Bar
        initial={false}
        animate={{ maxWidth: percentage + '%' }}
        transition={{ duration: 0.5 }}
        data-done={percentage >= 100}
        data-variant={variant}
        {...rest}
      />
    </Container>
  );
}

const Container = styled.div({
  width: '100%',
  height: '0.75rem',
  borderRadius: '0.5rem',
  backgroundColor: theme.color.uiTrans10,
  overflow: 'hidden',
});

export const Bar = styled(motion.div)({
  borderRadius: '0.5rem',
  height: '0.75rem',

  '&[data-done="false"]': {
    background: theme.color.orange100,
  },

  '&[data-done="true"]': {
    background: theme.color.green100,
  },

  '&[data-variant="info"]': {
    background: theme.color.indigo80,
  },

  '&[data-variant="paused"]': {
    background: theme.color.purple50,
  },

  '&[data-variant="error"]': {
    background: theme.color.red100,
  },
});

const SlidingContainer = styled.div({
  width: '100%',
  height: pxToRem(6),
  borderRadius: pxToRem(8),
  backgroundColor: theme.color.uiTrans10,
  overflow: 'hidden',
});

const SlidingBar = styled(motion.div)({
  borderRadius: pxToRem(8),
  height: pxToRem(6),

  '&[data-variant="success"]': {
    background: theme.color.green100,
  },

  '&[data-variant="error"]': {
    background: theme.color.red100,
  },

  '&[data-variant="info"]': {
    background: theme.color.indigo100,
  },
});
